/**
 * useFindingSliceWindow — load a wider slice window for a single finding.
 *
 * Used by FindingPreviewModal to give more scrollable slice context on demand.
 * Shares the same load mechanism as useFindingSlices (get_study_bbox + metadata
 * + buildS3ImageIds + renderPixelsToCanvas + presetForFinding) but:
 *  - Operates on ONE finding with a caller-supplied radius (RADIUS_EXPANDED ≈ 12)
 *  - Loads center-first then outward so the key slice appears immediately
 *  - Returns keyIndex so the modal can start the scrubber at the right position
 *  - Cancels on unmount
 *
 * Duplication note: the render loop mirrors useFindingSlices intentionally — the
 * loader is the 510(k)-sensitive "clean pixels" code and a focused parallel hook
 * is safer than a deep refactor of the gallery loader. The shared low-level
 * utilities (buildS3ImageIds, renderPixelsToCanvas, presetForFinding) are already
 * extracted; the per-finding async logic (~50 lines) is the only duplication.
 *
 * NO markings, NO bboxes — clean pixels only (510(k) constraint).
 */

import { useState, useEffect, useMemo } from "react";
import { initializeCornerstone } from "../utils/dicomLoader";
import {
  buildS3ImageIds,
  fetchStudyMetadata,
  StudyNotCanonicalizedError,
  type DicomInstanceInfo,
} from "../utils/dicomMetadataLoader";
import { getApiBaseUrl } from "../config/api";
import { renderPixelsToCanvas, type PixelDataArray } from "../utils/renderPixelsToCanvas";
import { presetForFinding } from "../utils/findingWindow";
import * as cornerstone from "@cornerstonejs/core";
import { assertKeyImageIdentity, type ExpectedImageIdentity } from "../utils/keyImageIdentity";
import { registerCdnToken } from "../utils/cdnTokens";

// Modal renders at a larger size for context.
const MODAL_OUTPUT_SIZE = 512;

/** Decoded, identity-verified pixels retained so frames can be re-windowed locally. */
export interface RenderSource {
  pixelData: PixelDataArray;
  rows: number;
  columns: number;
}

/** Display window (HU). */
export interface DisplayWindow {
  center: number;
  width: number;
}

export interface FindingSliceWindowState {
  frames: string[];
  /** Retained pixels aligned with frames[] (absent before the first frame). */
  sources?: RenderSource[];
  /** Index within frames[] corresponding to the key slice (starts centered). */
  keyIndex: number;
  loading: boolean;
  error?: string;
  /** Some optional neighboring slices failed; the verified key slice is unaffected. */
  contextError?: string;
}

/**
 * Load a wider window of slices for a single finding.
 *
 * @param studyId    Study identifier
 * @param _deviceId  Reserved — unused but kept for signature parity with useFindingSlices
 * @param condition  Finding condition path (e.g. "lungs_pleura_diaphragm/atelectasis")
 * @param topSlice   SOPInstanceUID of the key slice (may have ".dcm" suffix)
 * @param radius     Half-width of the window (e.g. 12 → up to 25 frames)
 * @param enabled    Set false to skip loading (e.g. when modal is closed)
 * @param windowOverride  Optional display window. Changing it re-renders the retained,
 *                   already-verified pixels; it never re-downloads or re-verifies.
 */
export function useFindingSliceWindow(
  studyId: string,
  _deviceId: string,
  condition: string,
  topSlice: string | null,
  radius: number,
  enabled = true,
  expectedIdentity?: ExpectedImageIdentity,
  windowOverride?: DisplayWindow | null,
): FindingSliceWindowState {
  const [state, setState] = useState<FindingSliceWindowState>({
    frames: [],
    keyIndex: 0,
    loading: true,
  });

  useEffect(() => {
    if (!enabled || !studyId || !condition || !topSlice) {
      setState({ frames: [], keyIndex: 0, loading: false });
      return;
    }

    // Reset to loading whenever inputs change.
    setState({ frames: [], keyIndex: 0, loading: true });

    const token = document.cookie.match(/a2z_auth_token=([^;]+)/)?.[1] ?? "";
    const userId = document.cookie.match(/a2z_user_id=([^;]+)/)?.[1] ?? "";
    let cancelled = false;

    const { studyUid, seriesUid, frameNumber } = expectedIdentity ?? {};
    Promise.all([
      fetch(`${getApiBaseUrl()}/api/get_study_bbox`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, study_id: studyId }),
      }).then((r) => r.json()),
      fetchStudyMetadata(userId, studyId, token).catch((e) => {
        if (e instanceof StudyNotCanonicalizedError) return null;
        throw e;
      }),
    ])
      .then(async ([bboxResp, metaResp]) => {
        if (cancelled) return;
        registerCdnToken(bboxResp.canonical_cdn_token);

        const cdnBaseUrl: string | undefined =
          bboxResp.canonical_cdn_base_url ?? metaResp?.canonical_cdn_base_url;
        const s3Series: Record<string, any[]> | undefined =
          metaResp?.series ?? bboxResp.s3_series ?? {};

        if (!cdnBaseUrl || !s3Series) {
          if (!cancelled) setState({ frames: [], keyIndex: 0, loading: false });
          return;
        }

        await initializeCornerstone();
        if (cancelled) return;

        // Normalise the key slice UID
        let sliceUid = topSlice;
        if (sliceUid.endsWith(".dcm")) sliceUid = sliceUid.slice(0, -4);

        // Locate the key slice in the series arrays (z_position-sorted by backend)
        let seriesInstances: DicomInstanceInfo[] | null = null;
        let keyIdx = -1;
        for (const [candidateSeriesUid, instances] of Object.entries(s3Series)) {
          if (seriesUid && candidateSeriesUid !== seriesUid) continue;
          const idx = (instances as any[]).findIndex((inst: any) => inst.instance_uid === sliceUid);
          if (idx !== -1) {
            if (
              studyUid &&
              ((instances[idx].study_uid !== undefined && instances[idx].study_uid !== studyUid) ||
                seriesInstances ||
                instances.filter((inst) => inst.instance_uid === sliceUid).length !== 1)
            ) {
              throw new Error("Selected image identity is mismatched or ambiguous.");
            }
            seriesInstances = instances as DicomInstanceInfo[];
            keyIdx = idx;
            if (!studyUid) break;
          }
        }

        if (!seriesInstances || keyIdx === -1) {
          if (!cancelled) setState({ frames: [], keyIndex: 0, loading: false });
          return;
        }

        // Wider window clamped to series bounds
        const start = Math.max(0, keyIdx - radius);
        const end = Math.min(seriesInstances.length - 1, keyIdx + radius);
        const windowInstances = seriesInstances.slice(start, end + 1);
        const centerIdx = keyIdx - start;

        const preset = presetForFinding(condition);

        const sources: (RenderSource | undefined)[] = Array.from({
          length: windowInstances.length,
        });
        const renderInstance = async (inst: DicomInstanceInfo, index: number): Promise<string> => {
          const [baseImageId] = buildS3ImageIds(cdnBaseUrl, [inst]);
          // Cornerstone's installed wadouri parser uses one-based frame numbers.
          const imageId = frameNumber ? `${baseImageId}&frame=${frameNumber}` : baseImageId;
          const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
          if (studyUid) assertKeyImageIdentity(image, inst, { studyUid, seriesUid, frameNumber });
          const pixelData = image.getPixelData() as PixelDataArray;
          sources[index] = { pixelData, rows: image.rows, columns: image.columns };
          const canvas = renderPixelsToCanvas(
            pixelData,
            image.rows,
            image.columns,
            preset.center,
            preset.width,
            MODAL_OUTPUT_SIZE,
          );
          return canvas.toDataURL();
        };

        // Center-first: key slice loads and publishes immediately
        const frames: (string | undefined)[] = Array.from({ length: windowInstances.length });
        frames[centerIdx] = await renderInstance(windowInstances[centerIdx], centerIdx);
        if (cancelled) return;

        // Publish single key-slice frame; scrubber shows it right away
        setState({
          frames: [frames[centerIdx] as string],
          sources: [sources[centerIdx] as RenderSource],
          keyIndex: 0,
          loading: false,
        });

        // Expand outward (±1, ±2, …), publishing a growing contiguous slice-ordered window.
        // Neighbors are optional context: a failed or unverified neighbor is excluded and
        // stops expansion on that side, but never clears the verified key slice.
        const blocked = { lower: false, upper: false };
        let contextError: string | undefined;
        for (let off = 1; off <= radius; off++) {
          for (const [side, i] of [
            ["lower", centerIdx - off],
            ["upper", centerIdx + off],
          ] as const) {
            if (blocked[side] || i < 0 || i >= windowInstances.length) continue;
            try {
              frames[i] = await renderInstance(windowInstances[i], i);
            } catch (error: unknown) {
              if (cancelled) return;
              blocked[side] = true;
              contextError = error instanceof Error ? error.message : "Nearby slice unavailable";
              setState((previous) => ({ ...previous, contextError }));
              continue;
            }
            if (cancelled) return;

            // Publish the contiguous run around the center
            let lo = centerIdx;
            let hi = centerIdx;
            while (lo - 1 >= 0 && frames[lo - 1] !== undefined) lo--;
            while (hi + 1 < frames.length && frames[hi + 1] !== undefined) hi++;
            const contiguous = frames.slice(lo, hi + 1) as string[];
            // keyIndex within this contiguous window
            setState({
              frames: contiguous,
              sources: sources.slice(lo, hi + 1) as RenderSource[],
              keyIndex: centerIdx - lo,
              loading: false,
              contextError,
            });
          }
          if (cancelled) return;
        }
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setState({
            frames: [],
            keyIndex: 0,
            loading: false,
            error: error instanceof Error ? error.message : "Image unavailable",
          });
      });

    return () => {
      cancelled = true;
    };
  }, [
    studyId,
    condition,
    topSlice,
    radius,
    enabled,
    expectedIdentity?.studyUid,
    expectedIdentity?.seriesUid,
    expectedIdentity?.frameNumber,
  ]);

  const overrideCenter = windowOverride?.center;
  const overrideWidth = windowOverride?.width;
  const frames = useMemo(() => {
    if (overrideCenter === undefined || overrideWidth === undefined || !state.sources)
      return state.frames;
    return state.sources.map((source) =>
      renderPixelsToCanvas(
        source.pixelData,
        source.rows,
        source.columns,
        overrideCenter,
        overrideWidth,
        MODAL_OUTPUT_SIZE,
      ).toDataURL(),
    );
  }, [state.frames, state.sources, overrideCenter, overrideWidth]);

  return frames === state.frames ? state : { ...state, frames };
}
